/**
 * A deliberately partial MiniMessage reader, for preview only.
 *
 * The authority on what a message looks like is Adventure's `MiniMessage.miniMessage()`
 * running in the mod (`AdventurePlayerMessenger.render`). This file does not reimplement it
 * and must never be mistaken for it: it renders the SUBSET a browser can honestly show -
 * the sixteen named colours, hex colours, the five decorations, `<reset>` and `<newline>` -
 * and represents everything else as an inert, visible marker.
 *
 * That last part is the whole design. A preview that silently dropped `<gradient:...>` would
 * teach an admin that their gradient does nothing; a preview that crashed on it would make
 * the editor unusable for the messages that need it most. So an unsupported-but-valid tag
 * renders as a labelled chip that says "the server understands this, this preview does not",
 * and the text keeps flowing around it.
 *
 * Pure and DOM-free on purpose - every rule below is unit-testable without React.
 */

/** The sixteen legacy colour names Adventure resolves, mapped to their chat hex values. */
export const NAMED_COLOURS: Readonly<Record<string, string>> = {
  black: '#000000',
  dark_blue: '#0000aa',
  dark_green: '#00aa00',
  dark_aqua: '#00aaaa',
  dark_red: '#aa0000',
  dark_purple: '#aa00aa',
  gold: '#ffaa00',
  gray: '#aaaaaa',
  grey: '#aaaaaa',
  dark_gray: '#555555',
  dark_grey: '#555555',
  blue: '#5555ff',
  green: '#55ff55',
  aqua: '#55ffff',
  red: '#ff5555',
  light_purple: '#ff55ff',
  yellow: '#ffff55',
  white: '#ffffff',
};

/** Decoration tags and their aliases, mapped to the style flag each one sets. */
const DECORATIONS: Readonly<Record<string, keyof Decorations>> = {
  bold: 'bold',
  b: 'bold',
  italic: 'italic',
  i: 'italic',
  em: 'italic',
  underlined: 'underlined',
  u: 'underlined',
  strikethrough: 'strikethrough',
  st: 'strikethrough',
  obfuscated: 'obfuscated',
  obf: 'obfuscated',
};

/**
 * Tags Adventure resolves that this preview cannot honestly render, listed so the editor can
 * say "valid, not previewable" instead of "unknown tag". Anything not here and not a
 * declared placeholder is reported as a genuine unknown, which is the diagnostic that
 * catches a typo like `<yelow>`.
 */
const KNOWN_UNPREVIEWABLE = new Set([
  'gradient',
  'rainbow',
  'transition',
  'click',
  'hover',
  'insertion',
  'font',
  'key',
  'lang',
  'translate',
  'selector',
  'sel',
  'score',
  'nbt',
  'data',
  'shadow_colour',
  'shadow_color',
  'pride',
]);

export interface Decorations {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underlined: boolean;
  readonly strikethrough: boolean;
  readonly obfuscated: boolean;
}

export interface Style extends Decorations {
  /** A `#rrggbb` string, or null for "the client's default text colour". */
  readonly colour: string | null;
}

export const PLAIN: Style = {
  colour: null,
  bold: false,
  italic: false,
  underlined: false,
  strikethrough: false,
  obfuscated: false,
};

export type Segment =
  | { readonly kind: 'text'; readonly text: string; readonly style: Style }
  /** A `<name>` the mod substitutes at send time. `declared` is false for a tag this message never resolves. */
  | { readonly kind: 'placeholder'; readonly name: string; readonly declared: boolean; readonly style: Style }
  /** A tag Adventure understands and this preview does not; shown, never dropped. */
  | { readonly kind: 'unpreviewable'; readonly tag: string; readonly raw: string; readonly style: Style }
  | { readonly kind: 'newline' };

export type DiagnosticLevel = 'warn' | 'info';

export interface Diagnostic {
  readonly level: DiagnosticLevel;
  readonly message: string;
}

export interface Preview {
  readonly segments: readonly Segment[];
  readonly diagnostics: readonly Diagnostic[];
  /** The message with every tag removed - what a client with no formatting would show. */
  readonly plainText: string;
}

/**
 * A MiniMessage tag: `<name>`, `<name:arg:arg>`, `</name>`, or the `<#rrggbb>` hex shorthand.
 * Anchored so it only matches at the scan position; the caller advances past whole tags.
 */
const TAG = /^<(\/?)([a-z0-9_#][a-z0-9_#:/.,'\- ]*)>/i;
const HEX = /^#[0-9a-f]{6}$/i;

function withDecoration(style: Style, flag: keyof Decorations, on: boolean): Style {
  return { ...style, [flag]: on };
}

/**
 * Reads `template` into styled segments plus the diagnostics the editor surfaces.
 *
 * `declaredPlaceholders` is the field's `x-placeholders` - the tags THIS message resolves.
 * A tag that is neither a MiniMessage tag nor one of those is a placeholder the mod will
 * never substitute, so the admin would ship literal `<plyer>` to players; that is the
 * warning the `@MiniMessage` annotation's javadoc asks the editor to raise.
 */
export function parseMiniMessage(template: string, declaredPlaceholders: readonly string[] = []): Preview {
  const declared = new Set(declaredPlaceholders);
  const segments: Segment[] = [];
  const diagnostics: Diagnostic[] = [];
  const seenUnknown = new Set<string>();
  const usedPlaceholders = new Set<string>();

  // A stack per style axis, so `</bold>` restores whatever was in force before `<bold>`
  // rather than resetting to plain - which is what Adventure does.
  let style: Style = PLAIN;
  const stack: Style[] = [];

  let text = '';
  const flushText = (): void => {
    if (text !== '') {
      segments.push({ kind: 'text', text, style });
      text = '';
    }
  };

  let index = 0;
  while (index < template.length) {
    const char = template[index] as string;

    // MiniMessage's escape: a backslash before `<` makes a literal angle bracket.
    if (char === '\\' && template[index + 1] === '<') {
      text += '<';
      index += 2;
      continue;
    }

    if (char === '\n') {
      flushText();
      segments.push({ kind: 'newline' });
      index += 1;
      continue;
    }

    if (char !== '<') {
      text += char;
      index += 1;
      continue;
    }

    const match = TAG.exec(template.slice(index));
    if (match === null) {
      // A bare `<` that opens nothing. Adventure treats it as text; so do we.
      text += char;
      index += 1;
      continue;
    }

    const [raw, slash, body] = match as unknown as [string, string, string];
    const closing = slash === '/';
    const parts = body.split(':');
    const name = (parts[0] as string).toLowerCase();

    flushText();
    index += raw.length;

    if (closing) {
      const restored = stack.pop();
      style = restored ?? PLAIN;
      if (restored === undefined) {
        diagnostics.push({ level: 'warn', message: `</${name}> closes a tag that was never opened.` });
      }
      continue;
    }

    if (name === 'reset') {
      stack.length = 0;
      style = PLAIN;
      continue;
    }

    if (name === 'newline' || name === 'br') {
      segments.push({ kind: 'newline' });
      continue;
    }

    // `<#ff0000>` and the long forms `<color:#ff0000>` / `<colour:red>` / `<c:red>`.
    const colourArg =
      HEX.test(name) || name in NAMED_COLOURS
        ? name
        : (name === 'color' || name === 'colour' || name === 'c') && parts.length > 1
          ? (parts[1] as string).toLowerCase()
          : null;
    if (colourArg !== null) {
      const hex = HEX.test(colourArg) ? colourArg.toLowerCase() : (NAMED_COLOURS[colourArg] ?? null);
      if (hex !== null) {
        stack.push(style);
        style = { ...style, colour: hex };
        continue;
      }
      diagnostics.push({ level: 'warn', message: `<${body}> is not a colour Minecraft knows.` });
      segments.push({ kind: 'unpreviewable', tag: name, raw, style });
      continue;
    }

    const decoration = DECORATIONS[name];
    if (decoration !== undefined) {
      // `<bold:false>` is Adventure's way of turning a decoration off inline.
      const on = parts[1] !== 'false';
      stack.push(style);
      style = withDecoration(style, decoration, on);
      continue;
    }

    if (declared.has(name)) {
      usedPlaceholders.add(name);
      segments.push({ kind: 'placeholder', name, declared: true, style });
      continue;
    }

    if (KNOWN_UNPREVIEWABLE.has(name)) {
      // These open a scope in Adventure, so push style to keep a later `</gradient>` balanced.
      stack.push(style);
      segments.push({ kind: 'unpreviewable', tag: name, raw, style });
      continue;
    }

    // Neither a tag Adventure resolves nor a placeholder this message declares: the mod
    // will leave it in the output verbatim, in front of players.
    segments.push({ kind: 'placeholder', name, declared: false, style });
    if (!seenUnknown.has(name)) {
      seenUnknown.add(name);
      diagnostics.push({
        level: 'warn',
        message: `<${name}> is not a placeholder this message resolves, so players would see it literally.`,
      });
    }
  }
  flushText();

  for (const placeholder of declared) {
    if (!usedPlaceholders.has(placeholder)) {
      diagnostics.push({ level: 'info', message: `<${placeholder}> is available here and unused.` });
    }
  }

  return { segments, diagnostics, plainText: plainTextOf(segments) };
}

function plainTextOf(segments: readonly Segment[]): string {
  return segments
    .map((segment) => {
      switch (segment.kind) {
        case 'text':
          return segment.text;
        case 'placeholder':
          return `<${segment.name}>`;
        case 'unpreviewable':
          return '';
        case 'newline':
          return '\n';
      }
    })
    .join('');
}

/** CSS for a segment's style. Exported so the preview component stays declarative. */
export function styleToCss(style: Style): React.CSSProperties {
  const decorations = [style.underlined ? 'underline' : '', style.strikethrough ? 'line-through' : ''].filter(
    (value) => value !== '',
  );
  return {
    ...(style.colour === null ? {} : { color: style.colour }),
    ...(style.bold ? { fontWeight: 700 } : {}),
    ...(style.italic ? { fontStyle: 'italic' } : {}),
    ...(decorations.length > 0 ? { textDecoration: decorations.join(' ') } : {}),
    ...(style.obfuscated ? { filter: 'blur(3px)' } : {}),
  };
}
